/* Split a Shopify/Printful description into: our made-to-order notice (current language only)
   and factual product details — dropping reseller/marketing boilerplate not meant for end customers. */
const DROP = [
    /must-have/i, /dreamed of/i, /who knew/i, /won't regret/i, /total must/i, /everything you've/i,
    /la tua scelta per/i, /tela versatile/i, /venditori e artisti/i, /ordine minimo/i, /farsi notare/i,
    /blank product sourced/i, /prodotto neutro proveniente/i, /sourced from/i,
    /GPSR/i, /general product safety/i, /sinden ventures/i, /oak inc/i, /anytown/i, /gpsr@/i, /limassol/i, /^and$/i, /^or$/i, /^e$/i, /^o$/i,
    /made especially for you/i, /thoughtful purchasing/i, /realizzato su richiesta/i,
    /age restrictions/i, /us customers should/i, /US market/i, /^ABNORMAL WORLD$/i, /^You can also write to us/i, /^\.$/,
];

/* Fit notes worth surfacing in the size guide (verified Printful statements). */
const FIT_NOTES = [
    /runs small/i, /size up/i, /size larger/i, /slightly sheer/i, /oversize/i, /vestibilità/i, /medium fit/i, /relaxed fit/i,
];

/* Verbatim Printful spec lines → Italian (materials, weights and certifications preserved). */
const IT = new Map(Object.entries({
    "100% organic ring-spun cotton": "100% cotone biologico ring-spun",
    "Fabric weight: 5.3 oz./yd.² (180 g/m²)": "Peso del tessuto: 180 g/m² (5,3 oz/yd²)",
    "Fabric weight: 4.2 oz./yd.² (142 g/m²)": "Peso del tessuto: 142 g/m² (4,2 oz/yd²)",
    "Single jersey": "Jersey singolo",
    "Medium fit": "Vestibilità regolare (medium fit)",
    "Set-in sleeves": "Maniche a giro (set-in)",
    "1 × 1 rib at collar": "Collo a costine 1×1",
    "Wide double-needle topstitch on the sleeves and bottom hems": "Orli di maniche e fondo con doppia cucitura larga",
    "Self-fabric neck tape (inside, back of the neck)": "Nastro paracollo interno nello stesso tessuto",
    "The fabric of this product holds certifications for its organic cotton content under GOTS (Global Organic Textile Standard) and OCS (Organic Content Standard)": "Tessuto certificato per il contenuto di cotone biologico GOTS (Global Organic Textile Standard) e OCS (Organic Content Standard)",
    "The fabric of this product is certified according to OEKO-TEX® STANDARD 100 (Certificate No. 1112055, Centexbel). The product is also PETA-Approved Vegan.": "Tessuto certificato OEKO-TEX® STANDARD 100 (certificato n. 1112055, Centexbel). Prodotto PETA-Approved Vegan.",
    "The sizes correspond to a smaller size in the US market, so US customers should order a size up.": "Le taglie seguono lo standard europeo (una taglia più piccola rispetto al mercato USA).",
    "100% combed and ring-spun cotton (Heather colors contain polyester)": "100% cotone pettinato ring-spun (i colori heather/mélange contengono poliestere)",
    "Pre-shrunk fabric": "Tessuto pre-ristretto",
    "Side-seamed construction": "Cuciture laterali",
    "Shoulder-to-shoulder taping": "Nastro di rinforzo spalla-spalla",
    "Disclaimer: The fabric is slightly sheer and may appear see-through, especially in lighter colors or under certain lighting conditions.": "Nota: il tessuto è leggermente trasparente, soprattutto nei colori chiari o con certe luci.",
    "EU Warranty: 2 years": "Garanzia legale UE: 2 anni",
    "Other compliance information: Meets the EU REACH requirements.": "Conformità: rispetta i requisiti del regolamento UE REACH.",
    "100% cotton face": "Esterno 100% cotone",
    "65% ring-spun cotton, 35% polyester": "65% cotone ring-spun, 35% poliestere",
    "Front pouch pocket": "Tasca a marsupio frontale",
    "Self-fabric patch on the back": "Etichetta posteriore nello stesso tessuto",
    "Matching flat drawstrings": "Cordini piatti in tinta",
    "3-panel hood": "Cappuccio a 3 pannelli",
    "Disclaimer: This hoodie runs small. For the perfect fit, we recommend ordering one size larger than your usual size.": "Nota: questa felpa veste piccolo. Per la vestibilità giusta consigliamo una taglia in più rispetto alla solita.",
}));

const textOf = (node) => {
    const clone = node.cloneNode(true);
    clone.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
    clone.querySelectorAll("p, li, div, h1, h2, h3").forEach((el) => el.append("\n"));
    return clone.textContent || "";
};

export function splitProductCopy(html, lang = "it") {
    if (!html || typeof window === "undefined") return { notice: "", details: [], fitNotes: [], sizeTable: "", untranslated: 0 };
    const doc = new DOMParser().parseFromString(html, "text/html");
    let notice = "";
    const section = doc.querySelector("section[data-abnormal-pod]");
    if (section) {
        const det = section.querySelector(`details[lang="${lang}"]`) || section.querySelector('details[lang="it"]');
        if (det) {
            det.querySelector("summary")?.remove();
            notice = det.innerHTML;
        }
        section.remove();
    }
    // A measurements table published in Shopify (if any) is the only size data we show.
    const table = doc.querySelector("table");
    const sizeTable = table ? table.outerHTML : "";
    table?.remove();

    const raw = textOf(doc.body);
    const lines = raw.split("\n").map((s) => s.replace(/^\s*•\s*/, "").trim()).filter(Boolean);
    const details = [];
    let untranslated = 0;
    for (const line of lines) {
        const parts = line.length > 160 && !line.includes("•") ? line.split(/(?<=[.!?])\s+(?=[A-ZÀ-Ý0-9])/) : [line];
        for (const s of parts) {
            const clean = s.replace(/\s+/g, " ").replace(/\s+\./g, ".").trim().replace(/^\.\s*/, "");
            if (clean.length < 3 || DROP.some((re) => re.test(clean))) continue;
            if (lang === "it") {
                const it = IT.get(clean);
                if (it) details.push(it);
                else { details.push(clean); if (/^[A-Za-z0-9 ,.%()×:/-]+$/.test(clean) && /\b(the|and|with|fabric|cotton)\b/i.test(clean)) untranslated += 1; }
            } else {
                details.push(clean);
            }
        }
    }
    const fitNotes = details.filter((d) => FIT_NOTES.some((re) => re.test(d)));
    return { notice, details, fitNotes, sizeTable, untranslated };
}

/* Pull production / delivery windows from the Shopify shipping policy (verified merchant data). */
export function parseShippingTimes(policyBody) {
    if (!policyBody) return null;
    const text = policyBody.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ");
    const prod = text.match(/prodott[oa] in\s*(\d+\s*[–-]\s*\d+)\s*giorni lavorativi/i);
    const italy = text.match(/Italia:\s*(\d+\s*[–-]\s*\d+)\s*giorni lavorativi/i);
    const europe = text.match(/Europa:\s*(\d+\s*[–-]\s*\d+)\s*giorni lavorativi/i);
    if (!prod && !italy) return null;
    return { production: prod?.[1]?.replace(/\s/g, ""), italy: italy?.[1]?.replace(/\s/g, ""), europe: europe?.[1]?.replace(/\s/g, "") };
}
